import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

// Angular modules
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog'; 
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';

// Constant configuration values
import { configurationValues } from '../../../constants/global'

// Custom services 
import { UtilsService } from '../../../services/utils/utils.service';
import { LanguageService } from '../../../services/language/language.service';
import { ControllerService } from '../../../services/controller/controller.service';

// Custom components
import { WaitingPopupComponent } from '../../popups/waiting-popup/waiting-popup.component';

// Custom date format adapter
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { CustomDateAdapterService } from '../../../services/custom-date-adapter/custom-date-adapter.service';
export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'DAY_MONTH_YEAR_LABEL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};


/* Specify the structure of the dictionary that will receive the dialog when being openned */
export interface DialogData {region:string, domain:string, data:any}

@Component({
  selector: 'app-corporate-license-popup',
  imports: [
    // Angular modules
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    MatMenuModule,
    MatListModule,
    MatDividerModule,
    MatIconModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    MatCheckboxModule,
    MatTabsModule,
    // Custom modules
    WaitingPopupComponent,
  ],
  // Custom date dapter
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapterService },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
  ],
  templateUrl: './corporate-license-popup.component.html',
  styleUrl: './corporate-license-popup.component.css'
})
export class CorporateLicensePopupComponent implements OnInit {

  public domain:string = '';
  public region:string = ''
  public refDict:any = null
  public inputDict:any = null
  public information:any=null;
  public users: { [key: string]: string[] } = {};

  // Mat menu values
  optionMenu_LanguageCode_value:string = ''
  optionMenu_LanguageCode_OptionList = configurationValues.languageList
  optionMenu_PasswordRotation_value:number = 0
  optionMenu_PasswordRotation_OptionList = configurationValues.passwordRotationList
  
  // Form controls
  formControl_isCollaborator = new UntypedFormControl(false);

  //  Study month
  formControl_endingTimestamp = new UntypedFormControl(null, [Validators.required]);
  startDate = new Date();
  //  Form Group
  formControlGroup = new UntypedFormGroup({
  });

  // Waiting popup info
  displayWaitingPopup = false
  waitingMessage = ''



  constructor(public utilsService:UtilsService,
              public languageService:LanguageService,
              public controllerService:ControllerService,
              public dialogRef: MatDialogRef<CorporateLicensePopupComponent>,
              @Inject(MAT_DIALOG_DATA) public dialogData: DialogData,
              public dateAdapter: DateAdapter<Date>
              )
  {
    // Get the domain and region
    this.region = dialogData.region
    this.domain = dialogData.domain
    // Copy the input dict to not modify the reference one
    this.refDict = dialogData.data
    this.inputDict = this.utilsService.deepCopyDict(dialogData.data)
    // Get the target subdictionaries
    this.information = this.inputDict.information
    this.users = this.inputDict.users
    // Initialize variables
    this.optionMenu_LanguageCode_value =  this.information.languageCode
    this.optionMenu_PasswordRotation_value = this.information.passwordRotation
  }

  ngOnInit(): void {
    this.initializeFormValues();
  }

  initializeFormValues(){
    if (this.information) {
      // Is collaborator
      this.formControl_isCollaborator.setValue(!!this.information.dbCollaborator);
      // Ending timestamp
      if (this.information.endingTimestamp) {
        this.formControl_endingTimestamp.setValue(new Date(this.information.endingTimestamp));
      }
    }
  }



  // ----- Button actions ----- //

  optionMenuClicked_LanguageCode(item:any){
    this.optionMenu_LanguageCode_value = item
  }

  optionMenuClicked_PasswordRotation(item:any){
    this.optionMenu_PasswordRotation_value = item
  }

  async buttonClicked_addUser(username: string){
    // Local verification
    const cleanUsername = username.trim().toLowerCase();
    if (!cleanUsername) return; // Do not add empty strings
    if (this.users[cleanUsername]) {
        // Opcional: Avisar si el usuario ya existe
        const errorMessage = `${this.languageService.Strings.errors.action_userAlreadyExists} (${cleanUsername})` 
        this.utilsService.displayErrorPopup(errorMessage)
        return;
    }
    // Create the user in the backend
    const newUserRoles = ['user'];
    // Display wating popup
    this.waitingMessage = this.languageService.Strings.messages.info_updatingLicense
    this.displayWaitingPopup = true
    // Update backend
    const resultDict:any = await this.controllerService.newUser_corporate(this.region, this.domain, username, newUserRoles)
    // Stop waiting popup
    this.displayWaitingPopup = false
    // Manage the result
    if(!resultDict['success']){
      // Display error poup
      return this.utilsService.displayErrorPopup(this.languageService.Strings.errors.action_unableToUpdateLicense)
    }
    // Add the item with the 'user' role
    this.users[cleanUsername] = newUserRoles;
    // Update the view
    this.users = { ...this.users };
    // Notify the user
    const infoMessage = this.languageService.Strings.messages.confirmation_userAdded
    return this.utilsService.displaySnackBar(infoMessage)
  }

  async checkboxClicked_updateRole(username: string, role: string, isChecked: boolean){
    // Create a copy of the user roles
    let rolesCopy = [...this.users[username]]
    if (isChecked) {
        // Add the rol if it does not exist
        if (!rolesCopy.includes(role)) {
            rolesCopy.push(role);
        }
    } else {
        // Remove the rol
        rolesCopy = rolesCopy.filter((r: string) => r !== role);
    }
    // Display wating popup
    this.waitingMessage = this.languageService.Strings.messages.info_updatingLicense
    this.displayWaitingPopup = true
    // Update backend
    const resultDict:any = await this.controllerService.updateUser_corporate(this.region, this.domain, username, rolesCopy)
    // Stop waiting popup
    this.displayWaitingPopup = false
    // Manage the result
    if(!resultDict['success']){
      // Display error poup
      this.utilsService.displayErrorPopup(this.languageService.Strings.errors.action_unableToUpdateLicense)
      // Visually reset the checkbox
      return this.users[username] = [...this.users[username]];
    }
    // Update the corresponding dict (this.users[username])
    this.users[username] = rolesCopy;
    // Update view
    return this.users[username] = [...this.users[username]];
  }

  async buttonClicked_deleteUser(username: string){
    if (this.users && this.users[username]) {
      // Display wating popup
      this.waitingMessage = this.languageService.Strings.messages.info_updatingLicense
      this.displayWaitingPopup = true
      // Update backend
      const resultDict:any = await this.controllerService.deleteUser_corporate(this.region, this.domain, username)
      // Stop waiting popup
      this.displayWaitingPopup = false
      // Manage the result
      if(!resultDict['success']){
        // Display error poup
        return this.utilsService.displayErrorPopup(this.languageService.Strings.errors.action_unableToUpdateLicense)
      }
      // Delete user
      delete this.users[username];
      // For detection
      this.users = { ...this.users };
    }
    // Ensure that the function allways returns
    return
  }

  cancelButtonClicked(){
    this.dialogRef.close()
  }




  acceptButtonClicked(){
    // Update the information of the formControls and buttons
    //  language code
    this.information.languageCode = this.optionMenu_LanguageCode_value
    //  password rotation
    this.information.passwordRotation = this.optionMenu_PasswordRotation_value
    //  ending timestamp
    const dateValue = this.formControl_endingTimestamp.value;
    if (dateValue instanceof Date){
      this.information.endingTimestamp = this.formatDateToBackendStyle(dateValue)
    }
    //  collaborator for database generation
    this.information.dbCollaborator = this.formControl_isCollaborator.value
    // Evaluate if there is any change
    const infoChanged = !this.areDictsEqual(this.refDict.information, this.inputDict.information)
    // Manage result based on information change
    //  Close without changes (equivalent to cancel)
    if(!infoChanged) return this.dialogRef.close() 
    //  Ask to store the changes
    const title = this.languageService.Strings.messages.pupupTitle_unsavedChanges
    const message = this.languageService.Strings.messages.pupupText_unsavedChanges
    this.utilsService.displayDecisionPopup(title, message, this.acceptButtonClicked_callback.bind(this))
  }


  async acceptButtonClicked_callback(shouldUpdate:boolean){
    // Close without changes (equivalent to cancel)
    if(!shouldUpdate) return this.dialogRef.close()
    // Store changes (there is at least one change)
    //  Display wating popup
    this.waitingMessage = this.languageService.Strings.messages.info_updatingLicense
    this.displayWaitingPopup = true
    //  Update backend
    const resultDict:any = await this.controllerService.updateDomain_corporate(
      this.domain, this.region, this.information.languageCode, this.information.endingTimestamp, 
      this.information.dbCollaborator, this.information.notificationEmail, 
      this.information.passwordRotation)
    //  Stop waiting popup
    this.displayWaitingPopup = false
    //  Manage the result
    if(!resultDict['success']){
      // Display error poup
      return this.utilsService.displayErrorPopup(this.languageService.Strings.errors.action_unableToUpdateLicense)
    }
    //  Update local dict to prevent erros
    this.refDict.information = this.utilsService.deepCopyDict(this.inputDict.information)
  }





  // ----- Utils ----- //

  formIsValid(){
    // Verify that the language code is corrected selected
    if(!this.optionMenu_LanguageCode_OptionList.includes(this.optionMenu_LanguageCode_value)){
      return false
    }
    // Verify that the form controsl are valid
    if(!this.formControl_endingTimestamp.valid || !this.formControl_isCollaborator.valid){
      return false
    }
    // Return true if everything is ok
    return true
  }
  
  getFormattedDate(isoString: string): string {
    if (!isoString) return '';
    const date = new Date(isoString);
    // Llamamos a tu lógica personalizada
    return this.dateAdapter.format(date, 'DAY_MONTH_YEAR_LABEL');
  }

  areDictsEqual(obj1: any, obj2: any): boolean {
    // 1. Identidad básica y primitivos (string, number, boolean)
    if (obj1 === obj2) return true;
    // 2. Si alguno no es objeto o es null, son diferentes
    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
      return false;
    }
    // 3. Si son Arrays: comparación rápida por JSON (importa el orden)
    // O puedes usar obj1.length === obj2.length && obj1.every(...)
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      return JSON.stringify(obj1.sort()) === JSON.stringify(obj2.sort());
    }
    // 4. Si uno es array y el otro no
    if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;
    // 5. Comparación de llaves del diccionario
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    for (const key of keys1) {
      // Si la llave no existe o el valor (recursivo) es distinto, fuera
      if (!Object.prototype.hasOwnProperty.call(obj2, key) || !this.areDictsEqual(obj1[key], obj2[key])) {
        return false;
      }
    }
    return true;
  }


  private formatDateToBackendStyle(date: Date): string {
    // 1. "Truco" para evitar problemas de zona horaria:
    // Creamos una fecha UTC usando los valores (año, mes, día) que el usuario ve en su pantalla.
    // Esto asegura que si eligió el día 31, sea el 31 a las 00:00 UTC, y no el 30 a las 23:00 por culpa de tu zona horaria local.
    const utcDate = new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0, 0, 0
    ));
    // 2. Obtenemos el ISO estándar: "2025-12-31T00:00:00.000Z"
    const isoString = utcDate.toISOString();
    // 3. Reemplazamos la parte final para que coincida con tu backend
    // Quitamos ".000Z" y ponemos "+00:00"
    return isoString.replace('.000Z', '+00:00');
  }


}
